<template>
  <div>
    <div class="dashboard-page-title">
      <div class="container-fluid">
        <h1>
          {{ $starLabel }} List

          <span v-if="manager">&ndash; {{ manager.company }}</span>
        </h1>
      </div>
    </div>

    <div class="container-fluid">
      <div class="dashboard-results-details">
        <div class="row">
          <div class="col-xs-12 col-sm-4">
            <router-link :to="{ name: 'talent.create', query: managerId ? { managerId } : {} }" class="btn btn-sm btn btn-primary">
              <i class="fa fa-plus"></i> Add {{$starLabel}}
            </router-link>
          </div>
        </div>
      </div>

      <user-list-filters
        class="margin-y-1"
        :set-filter-mutation="setFilter"
        :filters="filters"
        :apply-filters="handleApplyFiltersCallback">
        <template v-if="$userIsAdmin" v-slot:additional-filters>
          <div class="col-sm-6 col-md-4 spacer-b-2">
            <div class="form-group">
              <label class="sr-only">Visibility</label>
              <selectize
                ref="visibility"
                class="selectize selectize-single selectize-no-border"
                name="visibility"
                v-model="stateVisibility"
                placeholder="Visibility"
                :options="visibilityOptions"/>
            </div>
          </div>
        </template>
        <ladda-button
          slot="button"
          :click="handleApplyFiltersCallback"
          type="submit"
          class="btn btn-primary pull-right">
        Search
        </ladda-button>
      </user-list-filters>

      <div class="dashboard-block">
        <ul class="nav nav-tabs" id="myTabs" role="tablist" v-if="$userIsAdmin">
          <li :class="classForStatus('all')">
            <router-link :to="{ params: { status: 'all' }, query: $route.query }">All ({{ talentsCounts.all || 0 }})</router-link>
          </li>
          <li :class="classForStatus('active')">
            <router-link :to="{ params: { status: 'active' }, query: $route.query }">Active ({{ talentsCounts.active || 0 }})</router-link>
          </li>
          <li :class="classForStatus('unverified')">
            <router-link :to="{ params: { status: 'unverified' }, query: $route.query }">Unverified ({{ talentsCounts.unverified || 0 }})</router-link>
          </li>
          <li v-if="$vlastMode" :class="classForStatus('deactivated')">
            <router-link :to="{ params: { status: 'deactivated' }, query: $route.query }">Deactivated ({{ talentsCounts.deactivated || 0 }})</router-link>
          </li>
          <li :class="classForStatus('featured')">
            <router-link :to="{ params: { status: 'featured' }, query: $route.query }"><i class="fa fa-star"></i></router-link>
          </li>
          <li :class="classForStatus('trash')">
            <router-link :to="{ params: { status: 'trash' }, query: $route.query }"><i class="fa fa-trash"></i></router-link>
          </li>
          <li class="ml-auto text-right" v-if="$route.params.status==='unverified' && (!filters.keywords) && (filters.visibility === 'all')">
            <ladda-button
              slot="button"
              :click="handleSendActivationEmail"
              type="submit"
              class="btn btn-primary">
              Send Activation Email
            </ladda-button>
            <span class="help-block">This will notify all users who have not responded within the 24 hour period.</span>
          </li>
        </ul>
        <pickstar-loading v-if="fetchingTalent" class="m-t-lg"/>

        <no-results v-else-if="!loading && haveNoTalent">
          {{noTalentText}}
        </no-results>

        <ul v-else class="item-detail item-detail-profile">
          <talent-list-item
            v-for="talent in talents"
            :key="talent.id"
            :talent="talent"/>
        </ul>

        <lazy-loading
          v-if="!fetchingTalent"
          class="text-center"
          :loading="loading"
          :reached-bottom="handleGetNextTalentsCallback"
          ref="lazyLoading">
          <pickstar-loading slot="loading"/>
          <span slot="finished"></span>
        </lazy-loading>
      </div>
    </div>
  </div>
</template>
<script>
import { mapState, mapActions } from 'vuex'
import { appliesFilters, handleServerErrors, fetchResourceScope } from 'mixins'
import { stateMapper } from 'mixins/users/filters'
import LazyLoading from 'codium-lazy-loading'
import TalentListItem from './TalentListItem'
import UserListFilters from '../UserListFilters'
import Selectize from '../ext/codium/CodiumSelectize'
import { notify } from 'utils'

export default {
  mixins: [appliesFilters, handleServerErrors, fetchResourceScope, stateMapper],

  props: {
    status: {
      required: false,
      default: 'all'
    },
    managerId: {
      requred: false,
      default: null
    }
  },

  computed: {
    ...mapState('talent', ['talents', 'talentsCounts', 'pagination']),
    ...mapState('user', ['filters']),
    ...mapState('app', ['loading']),

    localQueryParams () {
      let params = this.queryParams

      if (this.managerId) {
        params.managerId = this.managerId
      }

      return params
    },

    haveNoTalent () {
      return this.talents.length === 0
    },

    noTalentText () {
      return `There are no ${this.$starLabelPlural}.`
    }
  },

  components: {
    UserListFilters,
    TalentListItem,
    LazyLoading,
    Selectize
  },

  data () {
    return {
      searchName: null,
      fetchingTalent: false,
      visibilityOptions: [
        { id: 'all', text: 'Public & Private' },
        { id: 'hidden', text: 'Hidden' },
        { id: 'public', text: 'Public' },
        { id: 'private', text: 'Private' }
      ]
    }
  },

  beforeDestroy () {
    this.resetTalents()
  },

  watch: {
    '$route' () {
      this.resetTalents()

      this.fetchingTalent = true

      if (this.$userIsAdmin) {
        this.getTalentsCounts(this.localQueryParams)
      }

      this.getTalents(this.localQueryParams)
        .then(() => {
          this.$nextTick(() => {
            if (!this.$refs.lazyLoading) {
              return
            }

            this.$refs.lazyLoading.$emit('reset')
          })
        })
        .catch(this.handleServerErrorsCallback)
        .finally(() => {
          this.fetchingTalent = false
        })
    }
  },

  created () {
    // Prevent managers from changing the status to affect the list. We do further prevention
    // by hard-coding the status that is searched as "all" both frontend and backend.
    if (this.$userIsManager && this.status !== 'all') {
      this.$router.push({ name: 'talent.index' })

      return
    }

    if (!this.$userIsAdmin) {
      this.getTalents(this.localQueryParams)
        .catch(this.handleServerErrorsCallback)
    }
  },

  methods: {
    ...mapActions('talent', ['getTalents', 'getNextTalents', 'getTalentsCounts', 'resetTalents']),
    ...mapActions('user', ['setFilter', 'resetFilters', 'sendActivationEmail']),

    setFilterMutation (payload) {
      return this.setFilter(payload)
    },

    handleGetNextTalentsCallback () {
      if (this.$userIsAdmin) {
        this.getTalentsCounts(this.localQueryParams)
      }
      return this.getNextTalents(this.localQueryParams)
    },

    handleSendActivationEmail () {
      return this.sendActivationEmail('talent').then((response) => {
        notify.success('Email Activation sent to all ‘Unverified users’.')
      }).catch((error) => {
        notify.error('Error occurred.')
        return Promise.reject(error)
      })
    },

    classForStatus (status) {
      if (this.status === status) {
        return 'active'
      }

      return ''
    }

  }
}
</script>
